/**
 * Rendu du panneau « tendances » de la carte : bloc verdict + graphe 15 jours.
 *
 * Le rendu est fait ici plutôt que côté Blade parce que le panneau est repeint à chaque
 * changement d'espèce sans rechargement : une seule implémentation, pas de duplication
 * entre PHP et JS.
 *
 * La charge utile est produite par App\Support\MushroomPotentialPanel.
 * Voir docs/superpowers/specs/2026-08-14-panneau-tendances-design.md
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

// Géométrie pensée pour le rail de 386 px. Le SVG est fluide, il ne défile jamais :
// une barre de défilement horizontale dans un conteneur déjà scrollable verticalement
// serait ingérable au pouce.
const VIEW = {
    // Largeur calee sur le rendu reel : rail de 386px, moins ses 24px de gouttiere et les
    // 20px de padding du bloc, soit 298px. Le viewBox est donc a l'echelle 1 et une taille
    // declaree ici est la taille reellement affichee. Avec un viewBox plus large, tout le
    // texte du graphe etait silencieusement reduit (340 -> 298 = -12%).
    width: 298,
    height: 174,
    // « Moyen » a 11,5px mesure environ 42px : la gouttiere doit l'accueillir sans rogner.
    padLeft: 54,
    padRight: 10,
    // Etait 32 pour loger la pilule « Meilleure periode ». Celle-ci ayant ete retiree,
    // cette reserve laissait un grand vide sous le titre.
    padTop: 8,
    padBottom: 42,
};

const PLOT_WIDTH = VIEW.width - VIEW.padLeft - VIEW.padRight;
const PLOT_HEIGHT = VIEW.height - VIEW.padTop - VIEW.padBottom;

function el(name, attrs = {}) {
    const node = document.createElementNS(SVG_NS, name);
    Object.keys(attrs).forEach((key) => node.setAttribute(key, attrs[key]));
    return node;
}

function xAt(index, count) {
    return count > 1 ? VIEW.padLeft + (index / (count - 1)) * PLOT_WIDTH : VIEW.padLeft;
}

function yAt(percent) {
    const clamped = Math.max(0, Math.min(100, percent));
    return VIEW.padTop + (1 - clamped / 100) * PLOT_HEIGHT;
}

/**
 * La couleur du tracé ne dépend que de l'ordonnée : un dégradé vertical à paliers durs
 * reproduit donc exactement la coloration par palier, pour un seul élément SVG au lieu
 * d'un segment coloré par jour.
 */
function buildBandGradient(bands, gradientId) {
    const gradient = el('linearGradient', {
        id: gradientId,
        gradientUnits: 'userSpaceOnUse',
        x1: 0,
        y1: VIEW.padTop,
        x2: 0,
        y2: VIEW.padTop + PLOT_HEIGHT,
    });

    // `bands` arrive du plus haut au plus bas. Deux arrêts par palier = transition franche.
    bands.forEach((band, index) => {
        const upperBound = index === 0 ? 100 : bands[index - 1].min - 1;
        gradient.appendChild(el('stop', {
            offset: `${(1 - Math.min(upperBound + 1, 100) / 100) * 100}%`,
            'stop-color': band.color,
        }));
        gradient.appendChild(el('stop', {
            offset: `${(1 - band.min / 100) * 100}%`,
            'stop-color': band.color,
        }));
    });

    const defs = el('defs');
    defs.appendChild(gradient);
    return defs;
}

function drawBandAxis(svg, bands) {
    bands.forEach((band, index) => {
        const upperBound = index === 0 ? 100 : bands[index - 1].min - 1;
        const top = yAt(Math.min(upperBound + 1, 100));
        const bottom = yAt(band.min);

        svg.appendChild(el('line', {
            x1: VIEW.padLeft,
            y1: bottom,
            x2: VIEW.padLeft + PLOT_WIDTH,
            y2: bottom,
            stroke: band.color,
            'stroke-width': 1,
            opacity: 0.18,
        }));

        const label = el('text', {
            x: VIEW.padLeft - 9,
            y: (top + bottom) / 2 + 4,
            'text-anchor': 'end',
            'font-size': 11.5,
            'font-weight': 600,
            fill: band.color,
        });
        label.textContent = band.label;
        svg.appendChild(label);
    });
}

function drawWindow(svg, points, highColor) {
    const inWindow = points.filter((point) => point.in_window);
    if (inWindow.length === 0) {
        return;
    }

    const startIndex = points.indexOf(inWindow[0]);
    const endIndex = points.indexOf(inWindow[inWindow.length - 1]);

    svg.appendChild(el('rect', {
        x: xAt(startIndex, points.length),
        y: VIEW.padTop,
        width: Math.max(xAt(endIndex, points.length) - xAt(startIndex, points.length), 1),
        height: PLOT_HEIGHT,
        fill: highColor,
        opacity: 0.08,
    }));

    [startIndex, endIndex].forEach((index) => {
        svg.appendChild(el('line', {
            x1: xAt(index, points.length),
            y1: VIEW.padTop,
            x2: xAt(index, points.length),
            y2: VIEW.padTop + PLOT_HEIGHT,
            stroke: highColor,
            'stroke-width': 1,
            'stroke-dasharray': '3 3',
            opacity: 0.6,
        }));
    });
}

function drawCurve(svg, points, gradientId, highColor) {
    const known = points
        .map((point, index) => ({ point, index }))
        .filter(({ point }) => point.percent !== null);

    if (known.length === 0) {
        return;
    }

    const line = known
        .map(({ point, index }, position) => {
            const command = position === 0 ? 'M' : 'L';
            return `${command}${xAt(index, points.length).toFixed(1)} ${yAt(point.percent).toFixed(1)}`;
        })
        .join(' ');

    const baseline = yAt(0).toFixed(1);
    const firstX = xAt(known[0].index, points.length).toFixed(1);
    const lastX = xAt(known[known.length - 1].index, points.length).toFixed(1);

    svg.appendChild(el('path', {
        d: `${line} L${lastX} ${baseline} L${firstX} ${baseline} Z`,
        fill: highColor,
        opacity: 0.10,
    }));

    svg.appendChild(el('path', {
        d: line,
        fill: 'none',
        stroke: `url(#${gradientId})`,
        'stroke-width': 2.8,
        'stroke-linejoin': 'round',
        'stroke-linecap': 'round',
    }));

    known.forEach(({ point, index }) => {
        svg.appendChild(el('circle', {
            cx: xAt(index, points.length),
            cy: yAt(point.percent),
            r: point.in_window ? 3.8 : 3,
            fill: point.color,
        }));
    });
}

function drawDateAxis(svg, points, highColor) {
    points.forEach((point, index) => {
        if (!point.labelled) {
            return;
        }

        const x = xAt(index, points.length);
        const emphasised = point.is_today || point.is_boundary;
        const fill = point.is_today ? '#232814' : (point.is_boundary ? highColor : '#747474');

        const day = el('text', {
            x,
            y: VIEW.padTop + PLOT_HEIGHT + 17,
            'text-anchor': 'middle',
            'font-size': 11.5,
            'font-weight': emphasised ? 700 : 400,
            fill,
        });
        day.textContent = point.label_day;
        svg.appendChild(day);

        // Le jour « aujourd'hui » porte déjà son libellé complet : pas de seconde ligne.
        if (!point.is_today) {
            const month = el('text', {
                x,
                y: VIEW.padTop + PLOT_HEIGHT + 31,
                'text-anchor': 'middle',
                'font-size': 10.5,
                'font-weight': emphasised ? 700 : 400,
                fill,
            });
            month.textContent = point.label_month;
            svg.appendChild(month);
        }
    });
}

/** Couleur du palier le plus haut, seule source pour l'aire, la zone favorable et le chrome. */
function highBandColor(bands) {
    const high = bands.find((band) => band.key === 'high');
    return (high && high.color) || '#2E7D32';
}

function renderChart(container, payload, gradientId) {
    container.innerHTML = '';

    const svg = el('svg', {
        viewBox: `0 0 ${VIEW.width} ${VIEW.height}`,
        class: 'potential-chart__svg',
        role: 'img',
        'font-family': "'Rethink Sans', sans-serif",
    });

    const highColor = highBandColor(payload.bands);

    svg.appendChild(buildBandGradient(payload.bands, gradientId));
    drawBandAxis(svg, payload.bands);
    drawWindow(svg, payload.points, highColor);
    drawCurve(svg, payload.points, gradientId, highColor);
    drawDateAxis(svg, payload.points, highColor);

    container.appendChild(svg);
}

/**
 * Peint le panneau d'une espèce.
 *
 * @param {string} panelId  id du conteneur (ex. "mushroom-detail-card-potential")
 * @param {object} payload  sortie de App\Support\MushroomPotentialPanel::build(), enrichie
 *                          des libellés traduits sous la clé `labels`
 */
export function renderPotentialPanel(panelId, payload) {
    const panel = document.getElementById(panelId);
    if (!panel || !payload) {
        return;
    }

    // Le SCSS lit cette variable pour le medaillon et la pilule : la couleur reste definie
    // une seule fois, cote PHP (MushroomPotentialBands::GREEN_HIGH).
    panel.style.setProperty('--potential-high', highBandColor(payload.bands));

    const chart = panel.querySelector('[data-potential-chart]');

    if (!chart) {
        return;
    }

    if (!payload.has_data) {
        chart.innerHTML = `<p class="mb-0">${payload.labels.noData}</p>`;
        return;
    }

    renderChart(chart, payload, `${panelId}-bands`);
}

export default renderPotentialPanel;
